import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";

function focusable(root: HTMLElement | null) {
  return Array.from(root?.querySelectorAll<HTMLElement>("*") ?? []).filter(element => element.matches('button, input, select, textarea, a[href], [tabindex="0"]') && !element.matches(":disabled") && element.tabIndex >= 0);
}

const PendingScheduleWrite = createContext<((pending: boolean) => void) | null>(null);

export function useScheduleBusy(): [boolean, (pending: boolean) => void] {
  const [busy, setBusy] = useState(false);
  const notify = useContext(PendingScheduleWrite);
  return [busy, (pending) => { setBusy(pending); notify?.(pending); }];
}

export function ScheduleOverlay({ children, onClose, sidePanel = false, returnFocusRef }: {
  children: ReactNode;
  onClose: () => void;
  sidePanel?: boolean;
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const [pending, setPending] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const prior = returnFocusRef?.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const focused = focusable(panel.current)[0];
    focused?.focus();
    return () => { if (prior?.isConnected) prior.focus(); };
  }, [returnFocusRef]);
  useEffect(() => {
    if (pending) {
      // Disabling the clicked control can move native browser focus to the document body.
      panel.current?.focus();
    } else {
      const alert = panel.current?.querySelector<HTMLElement>('[role="alert"]');
      if (alert) alert.focus();
      else if (document.activeElement === panel.current) focusable(panel.current)[0]?.focus();
    }
  }, [pending]);
  return (
    <PendingScheduleWrite.Provider value={setPending}>
      <div className={`schedule__overlay${sidePanel ? " manager-calendar-overlay" : ""}`} role="presentation"
        onClick={(event) => { if (!pending && event.target === event.currentTarget) onClose(); }}
        onKeyDown={(event) => {
          if (event.key === "Escape") { event.preventDefault(); if (!pending) onClose(); }
          if (event.key !== "Tab") return;
          const elements = focusable(panel.current);
          const first = elements[0], last = elements.at(-1);
          if (!first) { event.preventDefault(); return; }
          if (document.activeElement === panel.current) { event.preventDefault(); (event.shiftKey ? last : first)?.focus(); }
          else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }}>
        <div className="schedule__overlay-panel" ref={panel} tabIndex={-1} aria-busy={pending}>{children}</div>
      </div>
    </PendingScheduleWrite.Provider>
  );
}
